import { AsyncPipe } from '@angular/common';
import type { OnInit } from '@angular/core';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Injector,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import type { NonNullableFormBuilder } from '@angular/forms';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import type {
  OnyxDropdownOptionsSource,
  OnyxOption,
  OnyxSuggestion,
  OnyxTab,
} from '@onyx/angular';
import {
  ActionHelper,
  DropdownHelper,
  FormHelper,
  OnyxAutoFocusDirective,
  OnyxCheckboxComponent,
  OnyxDropdownComponent,
  OnyxFormGroupComponent,
  OnyxFormMode,
  OnyxSectionComponent,
  OnyxSuggestionsComponent,
  OnyxTabsComponent,
  OnyxTextFieldComponent,
  onyxTextFieldLengthValidator,
  OnyxTooltipDirective,
} from '@onyx/angular';
import { chain, isEqual, isObject, uniq } from 'lodash';
import type { Observable } from 'rxjs';
import {
  combineLatestWith,
  distinctUntilChanged,
  filter,
  map,
  of,
  ReplaySubject,
  shareReplay,
  skip,
  startWith,
  Subject,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { validate as validateUuid } from 'uuid';
import type { AuthStore } from '../../../../auth/common/stores/auth.store';
import type { OrderParametersFormGroup } from '../../../../common/components/forms/parameters-form/parameters-form.component';
import { DictionaryCode } from '../../../../common/enums/dictionary-code';
import { StatisticsType } from '../../../../common/enums/statistics-type';
import { CommonHelper } from '../../../../common/helpers/common.helper';
import { ValidationHelper } from '../../../../common/helpers/validation.helper';
import type {
  FleetType,
  FleetValidationByType,
} from '../../../../common/interfaces/validation/fleet-validation';
import { FleetIdentifierPipe } from '../../../../common/pipes/fleet-identifier.pipe';
import { DictionariesService } from '../../../../common/services/dictionaries.service';
import type { PreferencesService } from '../../../../common/services/preferences.service';
import { StatisticsService } from '../../../../common/services/statistics.service';
import { FleetCategory } from '../../../fleet/common/enums/fleet-category';
import { FleetState } from '../../../fleet/common/enums/fleet-state';
import { FleetHelper } from '../../../fleet/common/helpers/fleet.helper';
import { FleetValidationHelper } from '../../../fleet/common/helpers/fleet-validation.helper';
import type { Fleet } from '../../../fleet/common/interfaces/fleet';
import { FleetService } from '../../../fleet/common/services/fleet.service';
import { BusinessService } from '../../../management-panel/business/common/services/business.service';
import { ContractorsRoute } from '../../../management-panel/contractors/common/enums/contractors-route';
import { ContractorHelper } from '../../../management-panel/contractors/common/helpers/contractor.helper';
import type { Contractor } from '../../../management-panel/contractors/common/interfaces/contractor';
import { ContractorsService } from '../../../management-panel/contractors/common/services/contractors.service';
import { OrderSemiTrailerSize } from '../../common/enums/order-semi-trailer-size';
import { OrderStatus } from '../../common/enums/order-status';
import { OrderType } from '../../common/enums/order-type';
import { OrderFormService } from '../../common/services/order-form.service';

export type OrderBasicInformationFormGroup = ReturnType<
  typeof OrderBasicInformationFormComponent.buildForm
>;
type OrderBasicInformationForm = ReturnType<
  OrderBasicInformationFormGroup['getRawValue']
>;

@Component({
  selector: 'app-order-basic-information-form',
  imports: [
    OnyxSectionComponent,
    ReactiveFormsModule,
    OnyxDropdownComponent,
    AsyncPipe,
    OnyxCheckboxComponent,
    OnyxTabsComponent,
    OnyxFormGroupComponent,
    OnyxSuggestionsComponent,
    OnyxTextFieldComponent,
    OnyxTooltipDirective,
    OnyxAutoFocusDirective,
  ],
  templateUrl: './order-basic-information-form.component.html',
  styleUrl: './order-basic-information-form.component.scss',
})
export class OrderBasicInformationFormComponent implements OnInit {
  private readonly dictionariesService = inject(DictionariesService);
  private readonly injector = inject(Injector);
  private readonly fleetService = inject(FleetService);
  private readonly fleetIdentifierPipe = inject(FleetIdentifierPipe);
  private readonly contractorHelper = inject(ContractorHelper);
  private readonly statisticsService = inject(StatisticsService);
  private readonly orderFormService = inject(OrderFormService);
  private readonly contractorsService = inject(ContractorsService);
  private readonly translateService = inject(TranslateService);
  private readonly businessService = inject(BusinessService);
  private readonly fleetValidationHelper = inject(FleetValidationHelper);
  private readonly destroyRef = inject(DestroyRef);

  public readonly form = input.required<OrderBasicInformationFormGroup>();
  public readonly parametersForm = input.required<OrderParametersFormGroup>();
  public readonly validation =
    input.required<FleetValidationByType[FleetType]>();
  public readonly status = input.required<OrderStatus | null>();
  public readonly mode = input.required<OnyxFormMode>();
  public readonly readonlyVehicleRequirements = input.required<boolean>();

  protected readonly I18N = 'orders.orderForm.basicInformation';
  protected readonly SENDERS_SOURCE =
    this.contractorHelper.getContractorsSource();
  protected readonly LOADED_SEMI_TRAILER_SOURCE: OnyxDropdownOptionsSource<
    Fleet | string
  > = {
    list: (query, pagination) =>
      this.fleetService
        .searchFleet(query, {
          ...pagination,
          filters: [
            FleetHelper.createCategoryFilter([FleetCategory.SEMI_TRAILER]),
            FleetHelper.createStateFilter(FleetState.ACTIVE),
          ],
        })
        .pipe(
          combineLatestWith(this.isDedicated$),
          map(([result, isDedicated]) => ({
            ...result,
            options: chain(DropdownHelper.mapToOptions(result.options)!)
              .map(
                (option) =>
                  this.fleetValidationHelper.validateOrderFormSemiTrailer({
                    trailer: option.value,
                    loadedSemiTrailerUuid: this.loadedSemiTrailerValue(),
                    isDedicated,
                    option,
                  }).option!,
              )
              .thru((options) =>
                ValidationHelper.groupOptionsByIssue(options, {
                  error: 'issues.unavailable',
                }),
              )
              .value(),
          })),
        ),
    get: (uuid) => {
      if (!validateUuid(uuid)) {
        return {
          name: `${this.translateService.instant('labels.foreign')}: ${uuid}`,
          value: uuid,
        };
      }

      return this.fleetService.getFleet(uuid).pipe(
        map((trailer) => ({
          name: this.fleetIdentifierPipe.transform(trailer),
          value: trailer,
        })),
      );
    },
    idKey: 'uuid',
  };
  protected readonly IS_DEDICATED_OPTIONS: OnyxOption<boolean>[] =
    Object.values(OrderType).map((name) => ({
      name,
      value: name === OrderType.FTL,
    }));

  protected readonly isDedicated = this.orderFormService.isDedicated;
  protected readonly points = this.orderFormService.points;
  protected readonly firstLoadingPoint =
    this.orderFormService.firstLoadingPoint;
  protected readonly lastUnloadingPoint =
    this.orderFormService.lastUnloadingPoint;

  protected sender$ = this.orderFormService.sender$;
  protected senderLink$ = this.sender$.pipe(
    map((sender) =>
      CommonHelper.getPreviewLink(sender, (sender) =>
        this.contractorHelper.openModal(sender),
      ),
    ),
  );
  protected senderBranches$!: Observable<OnyxOption<string>[] | null>;
  protected ourBranches$!: Observable<OnyxOption<string>[]>;
  protected loadedSemiTrailerLoaded$ = new Subject<void>();
  protected loadedSemiTrailer$ = new ReplaySubject<Fleet | string | null>(1);

  protected vehicleTypeSuggestions$ = this.statisticsService
    .getStatistics(StatisticsType.VEHICLE_TYPE)
    .pipe(
      map((statistics): OnyxSuggestion<string>[] =>
        chain(statistics)
          .entries()
          .orderBy(([_, count]) => count, 'desc')
          .take(2)
          .filter(([_, count]) => count > 0)
          .map(([value]) => ({
            name: `${DictionaryCode.VEHICLE_TYPE}.${value}`,
            value,
          }))
          .value(),
      ),
    );
  protected semiTrailerSizes$ = this.dictionariesService
    .getDictionary(DictionaryCode.ORDER_SEMI_TRAILER_SIZE)
    .pipe(
      combineLatestWith(this.loadedSemiTrailer$),
      map(([options, loadedSemiTrailer]) =>
        options.map((option): OnyxTab<OrderSemiTrailerSize> => ({
          value: option.value,
          icon: {
            name:
              option.value === OrderSemiTrailerSize.AUTO
                ? 'refresh-outlined'
                : `semi-trailer-${option.value}-outlined`,
            size: 20,
          },
          iconColor: 'black',
          tooltip: option.name,
          disabled:
            option.value === OrderSemiTrailerSize.AUTO &&
            loadedSemiTrailer != null,
        })),
      ),
      shareReplay(1),
    );
  protected vehicleTypes$ = this.dictionariesService
    .getDictionary(DictionaryCode.VEHICLE_TYPE)
    .pipe(
      combineLatestWith(this.loadedSemiTrailer$),
      map(([options, loadedSemiTrailer]) =>
        options.map((option) => ({
          ...option,
          disabled: isObject(loadedSemiTrailer)
            ? loadedSemiTrailer?.generalInformation.type === option.value
            : false,
        })),
      ),
    );

  protected hasVanType$!: Observable<boolean>;
  protected dischargeTypes$!: Observable<OnyxOption<string>[]>;

  protected hasVanType = signal(false);

  private loadedSemiTrailerValue = signal<string | null>(null);
  private isDedicated$!: Observable<boolean>;

  private hasPaymentProcess = computed(
    () =>
      !!this.status() &&
      [
        OrderStatus.WAITING_FOR_PAYMENT,
        OrderStatus.PARTIALLY_PAID,
        OrderStatus.PAID,
      ].includes(this.status()!),
  );

  public ngOnInit() {
    const controls = this.form().controls;
    this.loadedSemiTrailerValue.set(controls.loadedSemiTrailer.getRawValue());

    const getControlsByKeys = (
      keys: (keyof OrderBasicInformationFormGroup['controls'])[],
    ) => chain(controls).pick(keys).values().value();

    effect(
      () => {
        if (!this.readonlyVehicleRequirements()) return;

        const VEHICLE_CONTROLS = getControlsByKeys([
          'loadedSemiTrailer',
          'vehicleTypes',
          'minSemiTrailerSize',
          'dischargeTypes',
        ]);
        ValidationHelper.disableControls(VEHICLE_CONTROLS, { reset: false });
      },
      { injector: this.injector },
    );

    effect(
      () => {
        if (!this.hasPaymentProcess()) return;

        const PAYMENT_PROCESS_CONTROLS = getControlsByKeys([
          'sender',
          'senderBranch',
          'ourBranch',
          'rateType',
          'rate',
          'vatRate',
        ]);
        ValidationHelper.disableControls(PAYMENT_PROCESS_CONTROLS, {
          reset: false,
        });
      },
      { injector: this.injector },
    );

    this.form()
      .valueChanges.pipe(
        startWith(undefined),
        map(() => this.form().getRawValue()),
        distinctUntilChanged((previous, current) => isEqual(previous, current)),
        combineLatestWith(
          toObservable(this.points, { injector: this.injector }),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(([value, points]) => {
        const form = this.form();
        const { dropPointUuid, loadedSemiTrailer, pickupPointUuid, isDropped } =
          value;
        const patch: Partial<OrderBasicInformationForm> = {};

        if (!loadedSemiTrailer) {
          patch.pickupPointUuid = null;
          patch.dropPointUuid = null;
        } else {
          const pickupExists = points.some(
            (point) => point.value.uuid === pickupPointUuid,
          );
          if (!pickupPointUuid || !pickupExists) {
            patch.pickupPointUuid =
              this.firstLoadingPoint()?.value.uuid ?? null;
          }

          if (isDropped) {
            const dropExists = points.some(
              (point) => point.value.uuid === dropPointUuid,
            );
            if (!dropPointUuid || !dropExists) {
              patch.dropPointUuid =
                this.lastUnloadingPoint()?.value.uuid ?? null;
            }
          } else {
            patch.dropPointUuid = null;
          }
        }

        if (Object.keys(patch).length > 0) form.patchValue(patch);
      });

    controls.loadedSemiTrailer.valueChanges
      .pipe(
        distinctUntilChanged(),
        skip(1),
        filter((trailer) => !trailer),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        controls.minSemiTrailerSize.setValue(null);

        FormHelper.reset([
          controls.isDropped,
          controls.vehicleTypes,
          controls.dischargeTypes,
        ]);
      });

    toObservable(this.isDedicated, { injector: this.injector })
      .pipe(
        combineLatestWith(this.loadedSemiTrailer$),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(([isDedicated, loadedSemiTrailer]) => {
        const isOurSemiTrailer = isObject(loadedSemiTrailer);
        const isDroppedControl = controls.isDropped;

        const setAndDisable = (value: boolean): void => {
          isDroppedControl.setValue(value);
          ValidationHelper.disableControls(isDroppedControl, { reset: false });
        };

        if (isOurSemiTrailer && !isDedicated) {
          setAndDisable(false);
        } else if (!isOurSemiTrailer && loadedSemiTrailer != null) {
          setAndDisable(true);
        } else {
          if (loadedSemiTrailer && !isDroppedControl.value) {
            isDroppedControl.setValue(false);
          }
          ValidationHelper.enableControls(isDroppedControl);
        }
      });

    this.isDedicated$ = toObservable(this.isDedicated, {
      injector: this.injector,
    });

    this.senderBranches$ = this.sender$.pipe(
      switchMap((sender) =>
        sender ? this.contractorsService.getBranches(sender) : of(null),
      ),
      tap((branches) => {
        ValidationHelper.toggleControls(controls.senderBranch, {
          enabled: branches != null && !this.hasPaymentProcess(),
          reset: false,
        });
        if (branches == null) return;

        const selectedBranch = branches.find(
          (branch) => branch.value === controls.senderBranch.value,
        );
        if (selectedBranch == null) {
          setTimeout(() => controls.senderBranch.setValue(branches[0].value));
        }
      }),
    );

    this.ourBranches$ = this.businessService.getBranches().pipe(
      tap((branches) => {
        ValidationHelper.toggleControls(controls.ourBranch, {
          enabled: branches.length !== 1 && !this.hasPaymentProcess(),
          reset: false,
        });
      }),
    );

    this.loadedSemiTrailerLoaded$
      .pipe(
        switchMap(() => this.loadedSemiTrailer$),
        skip(1),
      )
      .subscribe((loadedSemiTrailer) => {
        if (!isObject(loadedSemiTrailer)) {
          if (loadedSemiTrailer != null) {
            controls.minSemiTrailerSize.setValue(null);
            if (!this.hasVanType()) return;

            controls.vehicleTypes.setValue(
              (controls.vehicleTypes.value ?? []).filter(
                (type: string) => !type.includes(`${FleetCategory.VAN}-`),
              ),
            );
          }

          return;
        }

        const dischargeTypes = loadedSemiTrailer.cargoSpace.dischargeTypes!;
        controls.dischargeTypes.setValue(
          uniq([...dischargeTypes, ...controls.dischargeTypes.value]),
        );
      });

    this.loadedSemiTrailer$.subscribe((loadedSemiTrailer) => {
      if (this.readonlyVehicleRequirements()) return;

      if (!isObject(loadedSemiTrailer)) {
        controls.minSemiTrailerSize.enable();
        return;
      }

      const vehicleType = loadedSemiTrailer.generalInformation.type!;
      controls.vehicleTypes.setValue(
        uniq([vehicleType, ...controls.vehicleTypes.value]).filter(
          (type) => !type.includes(`${FleetCategory.VAN}-`),
        ),
      );

      const semiTrailerSize =
        loadedSemiTrailer.additionalParameters.semiTrailerSize!;
      controls.minSemiTrailerSize.setValue(semiTrailerSize as any);
      controls.minSemiTrailerSize.disable();
    });

    const validation$ = toObservable(this.validation, {
      injector: this.injector,
    });
    this.dischargeTypes$ = this.dictionariesService
      .getDictionary(DictionaryCode.DISCHARGE_TYPE)
      .pipe(
        combineLatestWith(validation$),
        map(([dischargeTypes, validation]) => {
          const field = validation['cargoSpace']?.fields['dischargeTypes'];
          const allowedDischargeTypes =
            field && 'values' in field ? field.values : [];

          return dischargeTypes.filter((type) =>
            allowedDischargeTypes.includes(type.value),
          );
        }),
        tap((dischargeTypes) => {
          if (this.readonlyVehicleRequirements()) return;

          ValidationHelper.toggleControls(controls.dischargeTypes, {
            enabled: dischargeTypes.length > 0,
            reset: false,
          });

          if (controls.vehicleTypes.pristine) return;

          controls.dischargeTypes.setValue(
            dischargeTypes.map((type) => type.value),
          );
        }),
      );

    this.vehicleTypeSuggestions$
      .pipe(
        filter(
          (suggestions) =>
            this.mode() === OnyxFormMode.ADD &&
            suggestions.length === 1 &&
            !controls.loadedSemiTrailer.value &&
            !controls.vehicleTypes.value.length,
        ),
        map(([suggestion]) => suggestion.value as FleetType),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((vehicleType) =>
        controls.vehicleTypes.setValue([vehicleType]),
      );

    this.hasVanType$ = controls.vehicleTypes.valueChanges.pipe(
      startWith(controls.vehicleTypes.value),
      map((types) =>
        types?.length
          ? types.some((type) => type.startsWith(`${FleetCategory.VAN}-`))
          : false,
      ),
      shareReplay(1),
      takeUntilDestroyed(this.destroyRef),
    );
    this.hasVanType$.subscribe((hasVan) => {
      if (hasVan) {
        controls.loadedSemiTrailer.setValue(null);
        setTimeout(() =>
          controls.minSemiTrailerSize.setValue(OrderSemiTrailerSize.AUTO),
        );
      }
      this.hasVanType.set(hasVan);

      if (
        controls.loadedSemiTrailer.value ||
        this.readonlyVehicleRequirements()
      ) {
        return;
      }

      ValidationHelper.toggleControls(controls.minSemiTrailerSize, {
        enabled: !hasVan,
        reset: false,
      });
    });
  }

  protected changeSender(sender: Contractor | null): void {
    this.orderFormService.changeSender(sender);
  }

  protected addSender(): void {
    ActionHelper.openRoute(ContractorsRoute.ADD_CONTRACTOR);
  }

  protected editSender(): void {
    this.sender$
      .pipe(
        take(1),
        filter((sender) => sender != null),
      )
      .subscribe((sender) =>
        ActionHelper.openLink(
          ContractorsRoute.EDIT_CONTRACTOR.replace(':uuid', sender.uuid),
        ),
      );
  }

  protected addExternalTrailer(registrationNumber: string): void {
    registrationNumber = registrationNumber.toUpperCase().trim();
    this.form().controls.loadedSemiTrailer.setValue(registrationNumber);
  }

  public static buildForm(
    fb: NonNullableFormBuilder,
    authStore: AuthStore,
    preferencesService: PreferencesService,
  ) {
    return fb.group({
      sender: fb.control<string | null>(null, [Validators.required]),
      senderBranch: fb.control<string | null>(null, [Validators.required]),
      ourBranch: fb.control(
        { value: authStore.business()!.uuid, disabled: true },
        [Validators.required],
      ),
      rateType: fb.control(preferencesService.defaultRateType(), [
        Validators.required,
      ]),
      rate: FormHelper.buildAmountForm(fb, preferencesService, {
        minValue: Number.EPSILON,
      }),
      vatRate: fb.control(preferencesService.defaultVatRate(), [
        Validators.required,
      ]),
      loadedSemiTrailer: fb.control<string | null>(null, [
        Validators.minLength(4),
        onyxTextFieldLengthValidator,
      ]),
      isDropped: fb.control<boolean | null>(null),
      pickupPointUuid: fb.control<string | null>(null),
      dropPointUuid: fb.control<string | null>(null),
      vehicleTypes: fb.control<FleetType[]>([], [Validators.required]),
      minSemiTrailerSize: fb.control<OrderSemiTrailerSize | null>(
        OrderSemiTrailerSize.AUTO,
        [Validators.required],
      ),
      dischargeTypes: fb.control<string[]>([], [Validators.required]),
      referenceNumber: fb.control<string | null>(null, [
        onyxTextFieldLengthValidator,
      ]),
    });
  }
}
